import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useMessage } from '../../contexts/MessageContext';
import Table from '../common/Table';
import Loading from '../common/Loading';
import { saveContractDownloadBlob, messageFromDownloadError } from '../../utils/contractDownload';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-800',
  voided: 'bg-gray-200 text-gray-500',
};

const ContractList = () => {
  const [rows, setRows] = useState([]);
  const [load, setLoad] = useState(true);
  const { logout } = useAuth();
  const { contractsEnabled, loading: featuresLoading } = useFeatures();
  const { showError, showInfo } = useMessage();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const downloadPdf = useCallback(
    async (contractUuid) => {
      try {
        const res = await axios.get(`${apiUrl}/contracts/${contractUuid}/pdf`, {
          headers: { 'x-auth-token': token },
          responseType: 'blob',
        });
        saveContractDownloadBlob(res, `contract-${contractUuid}`);
      } catch (err) {
        const msg = await messageFromDownloadError(err, 'Could not download contract');
        showError(msg);
      }
    },
    [token, showError]
  );

  useEffect(() => {
    if (!token) {
      logout();
      navigate('/login');
      return;
    }
    if (featuresLoading) return;
    if (!contractsEnabled) {
      navigate('/dashboard', { replace: true });
      return;
    }

    const fetchList = async () => {
      try {
        const res = await axios.get(`${apiUrl}/contracts`, {
          headers: { 'x-auth-token': token },
        });
        if (res.data.success) setRows(res.data.data || []);
        else setRows([]);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 403) {
          navigate('/dashboard', { replace: true });
          return;
        }
        showError(err.response?.data?.message || 'Failed to load contracts');
        setRows([]);
      } finally {
        setLoad(false);
      }
    };
    fetchList();
  }, [token, logout, navigate, contractsEnabled, featuresLoading, showError]);

  const columns = [
    {
      key: 'quote.quote_number',
      label: 'Quote #',
      sortable: true,
      render: (v) => v || '—',
    },
    {
      key: 'customer.name',
      label: 'Customer',
      sortable: true,
      render: (v) => v || '—',
    },
    {
      key: 'status',
      label: 'Contract',
      sortable: true,
      render: (v) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
            STATUS_COLORS[v] || 'bg-gray-100 text-gray-700'
          }`}
        >
          {v || '—'}
        </span>
      ),
    },
    { key: 'version', label: 'Ver.', sortable: true },
    {
      key: 'accepted_at',
      label: 'Accepted',
      sortable: true,
      type: 'date',
      render: (_, row) =>
        row.accepted_at ? (
          <span className="text-sm">
            {new Date(row.accepted_at).toLocaleString()}
            {row.signer_name ? (
              <span className="block text-xs text-gray-500">{row.signer_name}</span>
            ) : null}
          </span>
        ) : (
          '—'
        ),
    },
    { key: 'createdAt', label: 'Created', sortable: true, type: 'date' },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, row) => (
        <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
          {row.quote?.uuid && (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 text-xs underline"
              onClick={() => navigate(`/quotes/${row.quote.uuid}`)}
            >
              Open quote
            </button>
          )}
          {row.pdf_ready && (
            <button
              type="button"
              className="text-gray-800 hover:text-black text-xs underline"
              onClick={() => downloadPdf(row.uuid)}
            >
              Download contract
            </button>
          )}
          {row.share_token && (
            <button
              type="button"
              className="text-amber-700 hover:text-amber-900 text-xs underline"
              onClick={() => {
                const url = `${window.location.origin}/contract/view/${row.share_token}`;
                navigator.clipboard.writeText(url);
                showInfo('Customer contract link copied');
              }}
            >
              Copy link
            </button>
          )}
        </div>
      ),
    },
  ];

  if (featuresLoading || load) return <Loading />;

  if (!contractsEnabled) return null;

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Contracts</h2>
        <p className="text-sm text-gray-500 max-w-xl text-right">
          Downloads are PDF copies of the contract (same content as when it was created). If the server cannot render PDF,
          you may get an HTML file instead—open it and use Print → Save as PDF. Acceptance is recorded with name and time
          in the list.
        </p>
      </div>
      <Table
        data={rows}
        columns={columns}
        defaultSortKey="createdAt"
        pageSize={15}
        paginated
        rowKey="uuid"
      />
    </div>
  );
};

export default ContractList;
